import React, { useEffect, useState } from "react";

// Semi-circle radial gauge. Single indicator = the filled arc.
// fill fraction = score/100 of the 180-degree sweep, with a red-to-green
// gradient ending in a rounded glowing cap. No needle.
export default function RadialGauge({
  score = 0,
  band = "green",
  size = 320,
  testId = "overall-score",
}) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const duration = 1400;
    const target = Math.max(0, Math.min(100, Number(score) || 0));
    const tick = (t) => {
      const k = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(target * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  const w = size;
  const h = size / 2 + 28;
  const cx = w / 2;
  const cy = size / 2 + 4;
  const r = size / 2 - 18;

  const startA = Math.PI;
  const endA = 0;
  const arcStartX = cx + r * Math.cos(startA);
  const arcStartY = cy + r * Math.sin(startA);
  const arcEndX = cx + r * Math.cos(endA);
  const arcEndY = cy + r * Math.sin(endA);
  const bgPath = `M ${arcStartX} ${arcStartY} A ${r} ${r} 0 0 1 ${arcEndX} ${arcEndY}`;

  const fullLen = Math.PI * r;
  const dash = (shown / 100) * fullLen;

  // Indicator dot sits exactly at the end of the filled arc segment.
  // Parametrise the arc: t in [0,1] sweeps from left (π) clockwise over the
  // top half to the right (0). SVG y axis is inverted, so the top half uses
  // y = cy - r*sin(angle).
  const t = Math.max(0, Math.min(1, shown / 100));
  const indicatorAngle = Math.PI - t * Math.PI;
  const indicatorX = cx + r * Math.cos(indicatorAngle);
  const indicatorY = cy - r * Math.sin(indicatorAngle);
  const bandColor =
    band === "red" ? "#EF4444" : band === "yellow" ? "#EAB308" : "#4ADE80";

  return (
    <div
      className="flex flex-col items-center w-full"
      data-testid={`${testId}-gauge`}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="block"
        style={{ width: "100%", maxWidth: w, height: "auto" }}
        aria-label="Greenlight Score gauge"
      >
        <defs>
          <linearGradient id="gaugeGradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#EF4444" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#EAB308" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#4ADE80" />
          </linearGradient>
          <filter id="gaugeGlow" x="-20%" y="-30%" width="140%" height="160%">
            <feGaussianBlur stdDeviation="4.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Unfilled muted track */}
        <path
          d={bgPath}
          stroke="#1a221c"
          strokeWidth="14"
          strokeLinecap="round"
          fill="none"
        />
        {/* Filled portion - the colored arc indicator, with glow */}
        <path
          d={bgPath}
          stroke="url(#gaugeGradient)"
          strokeWidth="14"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dash} ${fullLen}`}
          filter="url(#gaugeGlow)"
        />
        {/* Needle - line from the pivot to the indicator position.
            Shares math with the dot so they cannot disagree. */}
        <line
          x1={cx}
          y1={cy}
          x2={indicatorX}
          y2={indicatorY}
          stroke={bandColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          filter="url(#gaugeGlow)"
        />
        {/* Pivot at the center of the gauge */}
        <circle cx={cx} cy={cy} r="4.5" fill={bandColor} />
        {/* Indicator dot at the score position. Outer halo + solid core. */}
        <circle
          cx={indicatorX}
          cy={indicatorY}
          r="11"
          fill={bandColor}
          opacity="0.25"
        />
        <circle
          cx={indicatorX}
          cy={indicatorY}
          r="6"
          fill="#ffffff"
          stroke={bandColor}
          strokeWidth="2.5"
          filter="url(#gaugeGlow)"
        />
      </svg>
      <div className="mt-4 flex flex-col items-center text-center">
        <div className="gl-label">Overall Score</div>
        <div
          data-testid={testId}
          className="font-black text-white leading-none tracking-tighter mt-2 flex items-end justify-center gap-2"
        >
          <span
            style={{
              fontSize: `clamp(52px, 13vw, ${Math.round(size * 0.3)}px)`,
            }}
          >
            {Math.round(shown)}
          </span>
          <span
            className="text-[#6c7770] font-light pb-1 sm:pb-2"
            style={{
              fontSize: `clamp(16px, 3.5vw, ${Math.round(size * 0.1)}px)`,
            }}
          >
            / 100
          </span>
        </div>
      </div>
    </div>
  );
}
